﻿namespace eXpress.core {
	export interface IImageRef {
		/** Name (whatever that is...) */
		readonly name: string;

		/** The absolute URL to fetch a 'medium' representation of the image */
		readonly mediumUrl: string;

		/** The absolute URL to fetch a 'thumb' representation of the image */
		readonly thumbUrl: string;
	}

	export interface IPhotoImageRef extends IImageRef {

		/** Width of the raw hires image */
		readonly width: number;

		/** Height of the raw  hires image */
		readonly height: number;

		/** Date when the photo was taken */
		readonly dateTaken: Date;

		/** Get State of this photo */
		readonly uploadState: PhotoState;

		/** Get LastErrorMessage of this photo */
		readonly lastErrorMessage: string;

		/** Function to set State (and eg clear lastErrorMessage, broadcast event, ...) */
		readonly setState: (uploadState: PhotoState, lastErrorMessage?: string) => void;

		/** the filesize of the hires image */
		readonly fileSize: number;

		readonly storeType: StoreType;
	}

	export enum UploadDownloadQueueState {
		/** New UploadDownload queue started */
		Started,

		/** Items added to Queue */
		Changed,

		/** All items are uploaded or downloaded not yet verified */
		Loaded,

		/** All items are uploaded or downloaded and verified */
		Ready
	}

	enum PhotoState {
		NonSmartPhoto = 0,
		UploadQueued = 1,
		Uploading = 2,
		UploadFailed = 3,
		Uploaded = 4,
		Rejected = 5,
		Imported = 6,
	}

	export enum DownloadItemStates {
		WaitingForDownload = 0,
		Downloaded = 1,
		DownloadError = 2,
		Imported = 3,
		ImportError = 4,
		NotFound = 5
	}

	/** For communication with client download and the core.downloader */
	export interface IDownloadItem {
		/** eXpress mediastore file id*/
		readonly mediastoreFileId: string;
		/** Partner ref id*/
		readonly photoId: string;
		readonly name: string;
	}

	export interface IDownloadQueueItem {
		state: DownloadItemStates;
		errorMessage?: string;
		downloadedItem?: core.IDownloadItem;
		itemToDownload: core.partners.PartnerPhoto;
	}

	export class Downloader {
		public download: services.Download;
		public flow: string;

		public progressPopup: ProgressPopup;

		constructor(downloadService: services.DownloadService, flow: string, popup: ProgressPopup) {
			this.download = new services.Download(downloadService, this);
			this.flow = flow;
			this.progressPopup = popup;
		}

		/** Add photo's to the download queue */
		public addToQueueAsync(photos: core.partners.PartnerPhoto[]): JQueryPromise<Photo[]> {
			if (!photos || !photos.length)
				return $.Deferred<Photo[]>().resolve([]).promise();

			this.progressPopup.openAsync();

			return this.download.addToQueueAsync(photos, this.flow)
				.then(downloadItems => {
					return downloadItems.map(di => new Photo(
						di.downloadedItem!.mediastoreFileId,
						di.itemToDownload.name,
						di.itemToDownload.dateTaken,
						di.itemToDownload.dateModified,
						di.itemToDownload.dateUploaded,
						di.itemToDownload.description,
						di.itemToDownload.fileSize,
						null,
						di.itemToDownload.height,
						di.itemToDownload.mediumUrl,
						di.itemToDownload.originalFilename,
						di.itemToDownload.thumbUrl,
						di.itemToDownload.width,
						0,
						di.itemToDownload.mediumUrl,
						null));
				})
				.always(() => this.progressPopup.close(true));
		}

		// update uploadprogress screen
		public handleDownloadItem(totalItems: number, itemsDone: number): void {
			const amount = totalItems + 1 - itemsDone;
			this.progressPopup.updateProgress(amount);
		}
	}

	export type IPhotoSourceItem = core.IPhotoImageRef;

	export abstract class PhotoSourceItem implements IPhotoSourceItem {

		public name: string;
		public dateTaken: Date;
		public uploadState: PhotoState;

		public lastErrorMessage: string;
		public width: number;
		public height: number;
		public thumbUrl: string;
		public mediumUrl: string;
		public fileSize: number;
		public storeType: StoreType;

		constructor(name: string, dateTaken: Date, uploadState: PhotoState) {
			this.name = name;
			this.dateTaken = dateTaken;
			this.uploadState = uploadState;
		}

		public setState(uploadState: PhotoState, lastErrorMessage?: string): void {
			this.uploadState = uploadState;
			if (lastErrorMessage) {
				this.lastErrorMessage = lastErrorMessage;
			}
		}

		public abstract loadAsync(): JQueryPromise<any>;

		public toString(): string {
			return "PhotoImageRef:" + this.name;
		}
	}

	export class PartnerPhotoSourceItem extends PhotoSourceItem implements IPhotoSourceItem {

		public file: partners.PartnerPhoto;
		public orientation: number;

		constructor(file: partners.PartnerPhoto) {

			super(file.originalFilename, new Date(), PhotoState.NonSmartPhoto);

			this.file = file;
			this.orientation = 1; // Default: No rotation
			this.width = this.file.width;
			this.height = this.file.height;
			this.mediumUrl = this.file.mediumUrl;
			this.thumbUrl = this.file.thumbUrl;
			this.storeType = this.file.storeType;
		}

		public loadAsync(): JQueryPromise<void> {
			return $.Deferred<void>().resolve().promise();
		}

		public toString(): string {
			return "PartnerPhotoSourceItem:" + this.name + " type:" + StoreType[this.storeType] + " url:" + this.file.downloadUrl;
		}
	}
}
